import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useAuthContext } from '@/context/AuthContext';
// import { hasCompletedOnboarding } from '@/firebase/auth/checkUserStatus'; // Removed Firebase dependency

/**
 * Component that checks if a user needs to be redirected to onboarding
 * Must be wrapped in AuthContextProvider
 */
const OnboardingChecker = ({ children }) => {
  const { user } = useAuthContext();
  const router = useRouter();
  const [checked, setChecked] = useState(false);

  const exemptPaths = [
    '/onboard',          // Obviously skip check on onboarding page
    '/AuthPage',         // Auth pages should be accessible
    '/auth/callback',    // OAuth callback page
    '/dashboard',        // Allow dashboard access
    '/',                 // Landing page
    '/blog',             // Public content
    '/pricing'           // Public pages
  ];

  // Check if current path is exempt from onboarding redirect
  const isExemptPath = () => {
    const path = router.pathname;

    // Check if the current path is in the exempt list
    return exemptPaths.some(exemptPath => {
      // Exact match or starts with the exempt path
      return path === exemptPath || path.startsWith(`${exemptPath}/`);
    });
  };

  useEffect(() => {
    // Skip check if path is exempt or we've already checked
    if (isExemptPath() || checked) {
      return;
    }

    const checkOnboardingStatus = async () => {
      // Only check for logged in users
      if (!user) {
        setChecked(true);
        return;
      }

      try {
        // Check onboarding status from user object (assuming it's populated from Supabase profile)
        // The AuthContext transforms the Supabase user, but we might need to ensure it includes profile data
        // For now, let's assume if we have a user, we check a property or skip if not available

        // Check onboarding status from user metadata (Supabase standard)
        const onboardingCompleted = user.user_metadata?.onboarded === true || user.user_metadata?.onboarded === 'true';

        // LEGACY USER CHECK: If user was created before Feb 15, 2026, assume they are onboarded
        // This prevents existing users from being forced into the new flow
        const isLegacyUser = user.created_at && new Date(user.created_at) < new Date('2026-02-15');

        if (!onboardingCompleted && !isLegacyUser) {
          // User hasn't completed onboarding, redirect
          router.push('/onboard');
        } else {
          // User has completed onboarding, continue
          setChecked(true);
        }
      } catch (error) {
        // console.error('Error checking onboarding status:', error);
        // In case of error, let the user continue
        setChecked(true);
      }
    };

    checkOnboardingStatus();
  }, [user, router, checked]);

  // Render the children once we've checked onboarding status
  // or if the path is exempt from checks
  if (isExemptPath() || checked) {
    return <>{children}</>;
  }

  // Show a loading state while checking
  return <div>Loading...</div>;
};

export default OnboardingChecker; 